import { useAuth } from '@dwelvio/auth-react';
import React, { createContext, ReactNode, useContext, useEffect } from 'react';
import { IUserData } from '../types';
import { formatDate, formatNumber, formatTime } from '@dwelvio/dateutils';
import { useLazyQuery } from '@apollo/client/react';
import { GetUserByIdDocument, GetUserByIdQuery, GetUserByIdQueryVariables } from '../generated/graphql';

export type AppContextType = {
  tz: string;
  language: string;
  formatDate: (date: string | Date | null | undefined) => string;
  formatTime: (date: string | Date | null | undefined) => string;
  formatNumber: (number: number | undefined | null, decimals: number) => string;
  formatDateTime: (date: string | Date | null | undefined) => string;
};

export const AppContext = createContext<AppContextType>({
  tz: 'Europe/Berlin',
  language: 'sk',
  formatDate: () => {
    throw new Error('appContext not initialized');
  },
  formatTime: () => {
    throw new Error('appContext not initialized');
  },
  formatNumber: () => {
    throw new Error('appContext not initialized');
  },
  formatDateTime: () => {
    throw new Error('appContext not initialized');
  },
});

export const useAppCtx = (): AppContextType => useContext(AppContext);

interface AppContextProviderProps {
  children?: ReactNode;
}

/**
 * Component that provides alert functionality to the app.
 * App must be wrapped in this component.
 * @param autoClose Timeout in ms for closing each alert type. Default: { info: 5000, error: 5000, fatal: 30000 }
 * @returns
 */
export const AppContextProvider = (props: AppContextProviderProps): React.JSX.Element => {
  const { children } = props;
  const auth = useAuth<IUserData>();
  const [getUser, { loading, data: userData }] = useLazyQuery<GetUserByIdQuery, GetUserByIdQueryVariables>(GetUserByIdDocument, {});

  useEffect(() => {
    if (auth?.user?.id) {
      getUser({ variables: { userId: auth.user.id } });
    }
  }, [auth?.user?.id]);

  const user = userData?.getUserById ?? auth.user;

  const tz = user?.tz ?? 'Europe/Bratislava';
  const language = user?.lang ?? 'sk';
  const fmtDate = user?.fmtDate ?? 'DMY';
  const fmtTime = user?.fmtTime ?? 'H24';
  const fmtNum = user?.fmtNum ?? 'UK';

  return (
    <AppContext.Provider
      value={{
        tz,
        language,
        formatDate: (date: string | Date | null | undefined) => formatDate(date, fmtDate),
        formatTime: (date: string | Date | null | undefined) => formatTime(date, fmtTime),
        formatNumber: (number: number | undefined | null, decimals: number) => formatNumber(number, decimals, fmtNum),
        formatDateTime: (date: string | Date | null | undefined) => {
          return formatDate(date, fmtDate) + ' ' + formatTime(date, fmtTime);
        },
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export function useApp(): AppContextType {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppContextProvider');
  }
  return context;
}
